import { openRightDrawerTab, setZoomLevel } from '../features/ui/uiSlice';
import { setFloorPlan, setFloorPlanOpacity } from '../features/floorplan/floorplanSlice';
import { store, type AppDispatch } from '../store';
import { canvasChromeInsets, computeFitViewport } from './flatPlanCamera';
import { viewportStore } from '../store/viewportStore';
import { clearFloor3dBatchCache, scheduleFloor3dBatchBuild } from './floor3dBatchCache';
import { fetchBackendLosslessJsonLayout } from './dxfApi';
import { loadEquipment, clearDxfEquipmentOverlays } from '../features/equipment/equipmentSlice';
import { extractEquipmentFromDxfJson, processExtractedEquipment } from './dxfEquipmentExtractor';
import { setDxfEquipmentSource } from './dxfEquipmentSource';
import type { EquipmentInstance } from '../features/equipment/equipmentTypes';
import { floorRasterService } from './floorRasterService';
import type { FloorRasterDataset } from './floorRasterTypes';

export type LoadedBackendFloorPlan = Awaited<ReturnType<typeof fetchBackendLosslessJsonLayout>>;

function fitBoundsToCanvas(dispatch: AppDispatch, bounds: {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}) {
  const vp = viewportStore.getState();
  // Side panels overlay the canvas; default to open chrome (typical editor entry).
  const insets = canvasChromeInsets({ leftSidebarOpen: true, rightSidebarOpen: true });
  const fit = computeFitViewport(
    bounds,
    vp.canvasWidth || 1200,
    vp.canvasHeight || 800,
    0.9,
    insets
  );
  viewportStore.setState(fit);
  dispatch(setZoomLevel(fit.zoom));
}

function yieldToUi(): Promise<void> {
  return new Promise((resolve) => {
    window.requestAnimationFrame(() => {
      window.setTimeout(resolve, 0);
    });
  });
}

function waitForIdle(): Promise<void> {
  return new Promise((resolve) => {
    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(() => resolve(), { timeout: 2500 });
    } else {
      window.setTimeout(resolve, 250);
    }
  });
}

export async function applyLoadedFloorPlanToEditor(
  dispatch: AppDispatch,
  plan: LoadedBackendFloorPlan,
  options?: { floorPlanId?: string | null }
): Promise<void> {
  // Let the loading overlay paint "Rendering…" before the heavy Redux write.
  await yieldToUi();

  // Keep DXF linework vivid; fab tile slab is dimmed separately in 3D when DXF is present.
  dispatch(setFloorPlanOpacity(1));

  dispatch(
    setFloorPlan({
      name: plan.name,
      floorPlanId: options?.floorPlanId ?? null,
      polylines: plan.polylines,
      bounds: plan.bounds,
      scaleFactor: plan.scaleFactor,
      dxfUnit: plan.dxfUnit,
      dxfUnitsPerMeter: plan.dxfUnitsPerMeter,
      calibration: plan.calibration,
      millimetersPerCanvasUnit: plan.millimetersPerCanvasUnit,
      canvasUnitsPerMeter: plan.canvasUnitsPerMeter,
      layers: plan.layers,
      polylineLayers: plan.polylineLayers,
      polylineMeta: plan.polylineMeta,
      labels: plan.labels,
      transform: plan.transform,
    })
  );

  fitBoundsToCanvas(dispatch, plan.bounds);
  dispatch(openRightDrawerTab('dxf-layers'));

  // Invalidate any stale 3D cache. Actual 3D batches are built lazily when the
  // user switches to 3D (see use3dFloorBatches) so 2D first paint stays fast.
  clearFloor3dBatchCache();

  // Paint geometry first; equipment can follow on the next tick.
  await yieldToUi();

  let extracted: EquipmentInstance[] = [];
  try {
    extracted =
      plan.extractedEquipment ??
      extractEquipmentFromDxfJson(plan.document, plan.transform);
    if (extracted.length > 0) {
      const { templates, instances, templateOrder, instanceOrder } = processExtractedEquipment(extracted);
      // Reset equipment carried over from a previously loaded floor plan.
      dispatch(
        loadEquipment({
          templates,
          instances,
          templateOrder,
          instanceOrder,
          visible: false,
          showPorts: false,
        })
      );
    }
  } catch (err) {
    console.error('Failed to extract and load equipment overlay:', err);
  }
  // Overlays are generated on demand (View → Generate Overlays), so only the
  // source is retained here. Any overlays from a previous floor plan are
  // destroyed rather than left behind for a save or simulation to pick up.
  // The extraction is cached too: this path parses in a worker that drops the
  // entity tree, leaving a document stub nothing can be re-extracted from.
  setDxfEquipmentSource({
    document: plan.document,
    transform: plan.transform,
    extractedEquipment: extracted,
  });
  dispatch(clearDxfEquipmentOverlays({ clearReviewDecisions: true }));

  // Build the cheap Simulation Mode overview first, then let the legacy 3D
  // vector warmup use the worker budget. This avoids two large geometry jobs
  // competing immediately after import while keeping both paths non-blocking.
  const floorRasterVisualState = store.getState().floorplan;
  const rasterDataset: FloorRasterDataset = {
    sourceId: options?.floorPlanId ?? plan.name ?? 'local-floor-plan',
    polylines: plan.polylines,
    polylineLayers: plan.polylineLayers,
    polylineMeta: plan.polylineMeta,
    layers: plan.layers,
    labels: plan.labels,
    bounds: plan.bounds,
    hiddenEntityTypes: floorRasterVisualState.hiddenEntityTypes,
    opacity: floorRasterVisualState.opacity,
    showLabels: floorRasterVisualState.showLabels,
  };
  void waitForIdle()
    .then(() => floorRasterService.prewarmOverview(rasterDataset))
    .catch(() => {
      // Worker/OffscreenCanvas failure intentionally leaves vectors as fallback.
    })
    .then(() =>
      scheduleFloor3dBatchBuild({
        polylines: plan.polylines,
        polylineLayers: plan.polylineLayers,
        polylineEntityColors: plan.polylineMeta.map((m) => m?.color ?? 256),
        layers: plan.layers,
        visible: true,
        darkMode: false,
      })
    )
    .catch((err) => console.error('Prebuild 3D floor batches failed:', err));
}

export async function loadBackendFloorPlanById(params: {
  dispatch: AppDispatch;
  floorPlanId: string;
  baseUrl?: string;
  fallbackName?: string;
}): Promise<LoadedBackendFloorPlan> {
  const plan = await fetchBackendLosslessJsonLayout({
    floorPlanId: params.floorPlanId,
    baseUrl: params.baseUrl,
    fallbackName: params.fallbackName,
  });

  if (plan.polylines.length === 0) {
    throw new Error('No drawable geometry found in backend lossless JSON response.');
  }

  await applyLoadedFloorPlanToEditor(params.dispatch, plan, { floorPlanId: params.floorPlanId });
  return plan;
}
